import React, { useEffect, useRef, useState } from "react";
import BaseSwitch from "@reusables/BaseSwitch";
import BaseInput from "@reusables/BaseInput";

export interface ProductEntry {
    product: {
        id: number;
        name: string;
        code: string;
    };
    totalQuantity: number;
}

interface ReservationRowProps {
    entry: ProductEntry;
    isReserved: boolean;
    reservedQuantity: number;
    onToggle: (checked: boolean) => void;
    onQuantityCommit: (quantity: number | null) => void;
}

// Isolated row component to prevent re-renders of other rows
export default function ReservationRow({
    entry,
    isReserved,
    reservedQuantity,
    onToggle,
    onQuantityCommit
}: ReservationRowProps) {
    const [draftValue, setDraftValue] = useState<string>("");
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const pendingFocusRef = useRef(false);

    // Sync draft value with actual reserved quantity when not focused
    useEffect(() => {
        if (!isFocused && isReserved) {
            setDraftValue(reservedQuantity.toString());
        }
    }, [reservedQuantity, isReserved, isFocused]);

    // Focus input after enabling reservation via row click
    useEffect(() => {
        if (isReserved && pendingFocusRef.current) {
            pendingFocusRef.current = false;
            // Small delay to ensure input is enabled and rendered
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 50);
        }
    }, [isReserved]);

    const handleFocus = () => {
        setIsFocused(true);
        setDraftValue(reservedQuantity.toString());
    };

    const handleBlur = () => {
        setIsFocused(false);
        
        const trimmed = draftValue.trim();
        
        // Empty or invalid -> remove reservation
        if (trimmed === "") {
            onQuantityCommit(null);
            return;
        }

        const numericValue = parseFloat(trimmed);
        
        if (isNaN(numericValue) || numericValue <= 0) {
            onQuantityCommit(null);
            return;
        }

        // Clamp to max and commit
        const clampedValue = Math.min(numericValue, entry.totalQuantity);
        onQuantityCommit(clampedValue);
    };

    const handleChange = (value: string | number | null | undefined) => {
        // Allow any input during typing - no validation here
        const stringValue = value === null || value === undefined ? "" : String(value);
        setDraftValue(stringValue);
    };

    const handleRowClick = (e: React.MouseEvent) => {
        // Don't trigger if clicking on interactive elements
        const target = e.target as HTMLElement;
        if (target.closest("input") || target.closest("button") || target.closest('[role="switch"]')) {
            return;
        }

        // Only handle click when not reserved
        if (!isReserved && entry.totalQuantity > 0) {
            pendingFocusRef.current = true;
            onToggle(true);
        }
    };

    const displayValue = isFocused ? draftValue : (isReserved ? reservedQuantity.toString() : "");

    return (
        <div 
            className={`flex items-center w-full py-2.5 px-4 text-sm transition-colors hover:bg-white/60 group ${!isReserved && entry.totalQuantity > 0 ? "cursor-pointer" : ""}`}
            onClick={handleRowClick}
        >
            {/* Left: Toggle */}
            <div className="flex-shrink-0">
                <BaseSwitch
                    checked={isReserved}
                    onChange={(_, checked) => onToggle(checked)}
                    disabled={entry.totalQuantity === 0}
                />
            </div>

            {/* Center: Product info */}
            <div className="flex-1 ml-4 min-w-0">
                <span className="text-accent">{entry.product.code}</span>
                <span className="ml-3 text-accent truncate">{entry.product.name}</span>
            </div>

            {/* Right: Quantity */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-4 pr-1">
                <div className="min-w-[100px]">
                    <BaseInput
                        ref={inputRef}
                        value={isReserved ? displayValue : "—"}
                        type={isReserved ? "number" : "text"}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                inputRef.current?.blur();
                            }
                        }}
                        disabled={!isReserved}
                        hideArrows
                        sx={{
                            "& input": {
                                padding: "8px 12px",
                                textAlign: "right",
                                borderColor: isReserved ? undefined : "transparent",
                                backgroundColor: isReserved ? undefined : "transparent",
                                color: isReserved ? undefined : "#9ca3af",
                                cursor: isReserved ? undefined : "pointer"
                            }
                        }}
                    />
                </div>
                <span className="text-sm text-gray-400 whitespace-nowrap">/ {entry.totalQuantity}</span>
            </div>
        </div>
    );
}
